/**
 * Billing Settings Page
 *
 * Main landing page for billing configuration and setup.
 * Uses tab-based navigation for cleaner UX following JotAudit patterns.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBillingEntities, useBillingEntityOperations } from '@features/billing-entities';
import { useEnrollments } from '@features/enrollments';
import { FeeScheduleSection } from '@features/organizations/components/FeeScheduleSection';
import { ServiceFacilitiesTab, useServiceFacilities } from '@features/service-facilities';
import { useStediStatusProvider } from '@features/stedi-connection';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFeatureGate } from '@services/statsig';
import type { BillingEntityResponse } from '@store/generated/billing-entities';
import { useSnackbar } from 'notistack';

import { useOrgReadiness } from '../store';
import { extractApiErrorMessage } from '../utils/errorUtils';

import BillingMigrationTab from './BillingMigrationTab';
import { BillingSetupWizard } from './BillingSetupWizard';
import { EditBillingEntityModal } from './EditBillingEntityModal';
import { PayerEnrollmentsTab } from './PayerEnrollmentsTab';
import { SetDefaultIdentityModal } from './SetDefaultIdentityModal';
import { SetupProgressIndicator } from './SetupProgressIndicator';

type TabValue = 'overview' | 'identities' | 'facilities' | 'enrollments' | 'fee-schedule' | 'data-migration';

interface TabPanelProps {
  children?: React.ReactNode;
  value: TabValue;
  activeTab: TabValue;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, activeTab }) => {
  if (value !== activeTab) {
    return null;
  }
  return <Box sx={{ pt: 3 }}>{children}</Box>;
};

export default function BillingSettingsPage() {
  const theme = useTheme();
  const billingEnabled = useFeatureGate('jotbill');
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitialStep, setWizardInitialStep] = useState(0);
  const [wizardEntityId, setWizardEntityId] = useState<string | null>(null);
  const [syncingEntityId, setSyncingEntityId] = useState<string | null>(null);
  const [editingEntity, setEditingEntity] = useState<BillingEntityResponse | null>(null);
  const [settingDefaultEntity, setSettingDefaultEntity] = useState<BillingEntityResponse | null>(
    null,
  );

  // Get current tab from URL or default to overview
  const currentTab = useMemo<TabValue>(() => {
    const tab = searchParams.get('tab');
    if (tab && ['overview', 'identities', 'facilities', 'enrollments', 'fee-schedule', 'data-migration'].includes(tab)) {
      return tab as TabValue;
    }
    return 'overview';
  }, [searchParams]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: TabValue) => {
      setSearchParams({ tab: newValue });
    },
    [setSearchParams],
  );

  // Fetch billing data using provider hooks (no organizationId needed)
  const { status: stediStatus, isConnected, isLoading: stediLoading } = useStediStatusProvider();
  const { readiness, isLoading: readinessLoading, refetch: refetchReadiness } = useOrgReadiness();
  const { entities, isLoading: entitiesLoading, refetch: refetchEntities } = useBillingEntities();
  const { enrollments, isLoading: enrollmentsLoading } = useEnrollments();
  const { facilities } = useServiceFacilities();
  const { sync: syncEntity, syncLoading, setDefault, setDefaultLoading } = useBillingEntityOperations();

  // Get default entity for enrollments tab
  const defaultEntity = entities.find(e => e.is_default && e.stedi_synced);
  const defaultEntityId = defaultEntity?.billing_entity_id || null;

  const isLoading = stediLoading || readinessLoading || entitiesLoading || enrollmentsLoading;

  // Determine setup status
  const hasStediConnection = isConnected;
  const hasBillingEntities = entities.length > 0;
  const hasServiceFacilities = facilities.length > 0;
  const hasEnrollments = enrollments.length > 0;
  const needsSetup = !hasStediConnection || !hasBillingEntities;
  
  // Check if any entities need syncing
  const hasUnsyncedEntities = entities.some(e => !e.stedi_synced);

  if (!billingEnabled) {
    return (
      <Alert severity="info">
        Billing features are not enabled for your organization. Please contact support for access.
      </Alert>
    );
  }

  const handleOpenWizard = (initialStep = 0, entityId: string | null = null) => {
    setWizardInitialStep(initialStep);
    setWizardEntityId(entityId);
    setWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
    setWizardInitialStep(0);
    setWizardEntityId(null);
    // Refresh data after wizard completes
    refetchReadiness();
    refetchEntities();
  };

  const handleSyncEntity = async (entity: BillingEntityResponse) => {
    setSyncingEntityId(entity.billing_entity_id);
    try {
      await syncEntity({ billingEntityId: entity.billing_entity_id }).unwrap();
      // Refresh entities to get updated sync status
      refetchEntities();
      refetchReadiness();
      enqueueSnackbar(`Billing identity verified. You're ready to submit claims!`, {
        variant: 'success',
      });
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to verify billing identity. Please try again.',
      );
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 10000, // Show longer for errors
        style: { whiteSpace: 'pre-wrap' }, // Allow multi-line messages
      });
      console.error('Failed to sync entity:', error);
    } finally {
      setSyncingEntityId(null);
    }
  };

  const handleEditEntity = (entity: BillingEntityResponse) => {
    setEditingEntity(entity);
  };

  const handleCloseEditModal = () => {
    setEditingEntity(null);
  };

  const handleEntityUpdated = () => {
    refetchEntities();
    refetchReadiness();
  };

  const handleSetDefaultClick = (entity: BillingEntityResponse) => {
    setSettingDefaultEntity(entity);
  };

  const handleSetDefaultConfirm = async () => {
    if (!settingDefaultEntity) return;

    try {
      await setDefault({ billingEntityId: settingDefaultEntity.billing_entity_id }).unwrap();
      enqueueSnackbar(
        `${settingDefaultEntity.legal_name} is now your default billing identity`,
        { variant: 'success' },
      );
      setSettingDefaultEntity(null);
      refetchEntities();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to set default billing identity. Please try again.',
      );
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }
  };

  const handleCloseSetDefaultModal = () => {
    setSettingDefaultEntity(null);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Page Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 0.5,
            }}
          >
            Claim Submission Setup
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            Set up your practice to submit claims electronically to insurance companies
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetchReadiness()}
            disabled={isLoading}
            size="small"
          >
            Refresh
          </Button>
          {needsSetup && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayArrowIcon />}
              onClick={() => handleOpenWizard()}
              disabled={isLoading}
            >
              Start Setup
            </Button>
          )}
        </Box>
      </Box>

      {/* Progress Indicator */}
      <SetupProgressIndicator
        hasConnection={hasStediConnection}
        hasIdentities={hasBillingEntities}
        allIdentitiesVerified={hasBillingEntities && !hasUnsyncedEntities}
        hasRequiredEnrollments={hasEnrollments}
      />

      {/* Tabs Navigation */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'transparent',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 'auto',
              px: 3,
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="identities" label="Billing Identities" />
          <Tab value="facilities" label="Service Facilities" />
          <Tab value="enrollments" label="Payer Enrollments" />
          <Tab value="fee-schedule" label="Fee Schedule" />
          <Tab value="data-migration" label="Data Migration" />
        </Tabs>
      </Paper>

      {/* Overview Tab */}
      <TabPanel value="overview" activeTab={currentTab}>
        {/* Connection Status */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isConnected ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Clearinghouse Connection
                </Typography>
              </Box>
              {!isConnected && (
                <Button
                  variant="contained"
                  onClick={() => handleOpenWizard()}
                  startIcon={<PlayArrowIcon />}
                  size="small"
                >
                  Connect
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {isConnected
                ? 'Connected and ready to submit claims'
                : 'Not connected - Connect to start submitting claims'}
            </Typography>
            {isConnected && stediStatus?.account_label && (
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Chip label={stediStatus.account_label} size="small" color="primary" />
                <Chip label={stediStatus.mode || 'SHARED'} size="small" variant="outlined" />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Entities Summary */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {hasBillingEntities && !hasUnsyncedEntities ? (
                  <CheckCircleIcon color="success" />
                ) : hasBillingEntities ? (
                  <ErrorIcon color="warning" />
                ) : (
                  <ErrorIcon color="error" />
                )}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Billing Identities
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => setSearchParams({ tab: 'identities' })}
                size="small"
              >
                Manage
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {hasBillingEntities
                ? `${entities.length} ${entities.length === 1 ? 'identity' : 'identities'} configured${hasUnsyncedEntities ? ' (setup required)' : ''}`
                : 'No billing identities configured'}
            </Typography>
          </CardContent>
        </Card>

        {/* Service Facilities Summary */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {hasServiceFacilities ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="warning" />
                )}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Service Facilities
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => setSearchParams({ tab: 'facilities' })}
                size="small"
              >
                Manage
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {hasServiceFacilities
                ? `${facilities.length} ${facilities.length === 1 ? 'facility' : 'facilities'} configured`
                : 'No service facilities configured'}
            </Typography>
          </CardContent>
        </Card>

        {/* Enrollments Summary */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {hasEnrollments ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="warning" />
                )}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Payer Enrollments
                </Typography>
              </Box>
              {enrollments.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setSearchParams({ tab: 'enrollments' })}
                  size="small"
                >
                  View All
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {hasEnrollments
                ? `${enrollments.filter(e => e.status === 'LIVE').length} active ${enrollments.filter(e => e.status === 'LIVE').length === 1 ? 'enrollment' : 'enrollments'}`
                : 'No payer enrollments (most claims don\'t require this)'}
            </Typography>
          </CardContent>
        </Card>

        {/* About Electronic Claims */}
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              About Electronic Claims
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Electronic claims reach payers faster and have fewer errors than paper claims. Most
              claims are processed within 1-2 weeks of submission.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              To submit claims electronically, you need to:
            </Typography>
            <Box component="ol" sx={{ pl: 2, m: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Connect to a clearinghouse (routes claims to insurance companies)
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Add your billing identity (NPI and Tax ID)
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Verify your billing identity with the clearinghouse
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Enroll with payers that require it (if needed)
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Billing Identities Tab */}
      <TabPanel value="identities" activeTab={currentTab}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Manage Billing Identities
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Each identity represents an NPI and Tax ID combination for billing
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenWizard(1)}
            >
              Add Identity
            </Button>
          </Box>

          {/* Billing Identities List or Empty State */}
          {entities.length > 0 ? (
            <>
              {hasUnsyncedEntities && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Finish setting up your billing identity to start submitting claims. We need to verify your information with the clearinghouse first.
                </Alert>
              )}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {entities.map(entity => (
                <Box
                  key={entity.billing_entity_id}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: theme.palette.grey[200],
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:hover': {
                      borderColor: theme.palette.grey[300],
                    },
                  }}
                >
                  {/* Identity Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {entity.legal_name}
                      </Typography>
                      {entity.is_default && (
                        <Chip label="Default" size="small" color="primary" variant="outlined" sx={{ height: 20 }} />
                      )}
                      <Chip
                        label={entity.stedi_synced ? 'Ready' : 'Setup Required'}
                        size="small"
                        color={entity.stedi_synced ? 'success' : 'warning'}
                        sx={{ height: 20 }}
                      />
                    </Box>
                    
                    {entity.doing_business_as && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        DBA: {entity.doing_business_as}
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        NPI: {entity.billing_npi}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tax ID: {entity.tax_id_masked} ({entity.tax_id_type})
                      </Typography>
                      {entity.taxonomy_code && (
                        <Typography variant="caption" color="text.secondary">
                          Taxonomy: {entity.taxonomy_code}
                        </Typography>
                      )}
                      {entity.stedi_synced && entity.stedi_synced_at && (
                        <>
                          <Typography variant="caption" color="text.secondary">•</Typography>
                          <Typography variant="caption" color="success.main">
                            Verified {new Date(entity.stedi_synced_at).toLocaleDateString()}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {!entity.stedi_synced && (
                      <Tooltip title="Verify billing identity" arrow>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSyncEntity(entity)}
                            disabled={syncLoading || syncingEntityId === entity.billing_entity_id}
                          >
                            {syncingEntityId === entity.billing_entity_id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SyncIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {entity.stedi_synced && (
                      <Tooltip 
                        title={entity.is_default ? "Default billing identity" : "Set as default"} 
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => !entity.is_default && handleSetDefaultClick(entity)}
                            disabled={setDefaultLoading}
                            sx={{
                              color: entity.is_default ? 'success.main' : 'inherit',
                              '&.Mui-disabled': {
                                color: entity.is_default ? 'success.main' : undefined,
                              },
                              cursor: entity.is_default ? 'default' : 'pointer',
                              pointerEvents: entity.is_default ? 'none' : 'auto',
                            }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit identity" arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntity(entity)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete identity" arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          // TODO: Open delete confirmation
                          console.debug('Delete entity:', entity.billing_entity_id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
              </Box>
            </>
          ) : (
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                Add your billing identity
              </Typography>
              <Typography variant="body2" paragraph>
                Your billing identity tells insurance companies who to pay. You'll need:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
                <Typography component="li" variant="body2">
                  Your practice's NPI (10-digit provider ID)
                </Typography>
                <Typography component="li" variant="body2">
                  Tax ID (EIN or SSN)
                </Typography>
                <Typography component="li" variant="body2">
                  Billing address
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenWizard(1)}
                sx={{ mt: 1 }}
              >
                Add Billing Identity
              </Button>
            </Alert>
          )}
        </CardContent>
      </Card>
      </TabPanel>

      {/* Service Facilities Tab */}
      <TabPanel value="facilities" activeTab={currentTab}>
        <ServiceFacilitiesTab />
      </TabPanel>

      {/* Payer Enrollments Tab */}
      <TabPanel value="enrollments" activeTab={currentTab}>
        <PayerEnrollmentsTab
          defaultEntityId={defaultEntityId}
          onOpenWizard={handleOpenWizard}
        />
      </TabPanel>

      {/* Fee Schedule Tab */}
      <TabPanel value="fee-schedule" activeTab={currentTab}>
        <FeeScheduleSection />
      </TabPanel>

      {/* Data Migration Tab */}
      <TabPanel value="data-migration" activeTab={currentTab}>
        <BillingMigrationTab />
      </TabPanel>

      {/* Billing Setup Wizard Modal */}
      {wizardOpen && (
        <BillingSetupWizard
          open={wizardOpen}
          onClose={handleCloseWizard}
          initialStep={wizardInitialStep}
          existingEntityId={wizardEntityId}
        />
      )}

      {/* Edit Billing Entity Modal */}
      {editingEntity && (
        <EditBillingEntityModal
          open={!!editingEntity}
          onClose={handleCloseEditModal}
          entity={editingEntity}
          onUpdated={handleEntityUpdated}
        />
      )}

      {/* Set Default Identity Modal */}
      <SetDefaultIdentityModal
        open={!!settingDefaultEntity}
        entity={settingDefaultEntity}
        onClose={handleCloseSetDefaultModal}
        onConfirm={handleSetDefaultConfirm}
        isLoading={setDefaultLoading}
      />
    </Container>
  );
}
